import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";

export const PromosAddModal = ({
  isOpen,
  onClose,
  onAddTransaction,
  transactionDetails = [],
}) => {
  const [formData, setFormData] = useState({
    transactionType: "withdraw",
    amount: "",
    playerName: "",
    playerNumber: "",
    notes: "",
  });
  const [selectedPromo, setSelectedPromo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isDeposit = formData.transactionType === "deposit";
  const promoOptions = Array.isArray(transactionDetails) ? transactionDetails : [];

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        transactionType: "withdraw",
        amount: "",
        playerName: "",
        playerNumber: "",
        notes: "",
      });
      setSelectedPromo("");
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePromotionSelect = (value) => {
    if (!value || value === "select") {
      setSelectedPromo("");
      return;
    }
    setSelectedPromo(value);
    setFormData((prev) => ({ ...prev, notes: value }));
  };

  const handleSubmit = async () => {
    if (!formData.amount) {
      toast.error("Please enter an amount");
      return;
    }

    if (!isDeposit && !formData.notes.trim()) {
      toast.error("Please select a promotion");
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmitting(true);
    try {
      const success = await onAddTransaction({
        transactionType: formData.transactionType,
        amount,
        playerName: isDeposit ? "" : formData.playerName.trim(),
        playerNumber: isDeposit ? "" : formData.playerNumber,
        notes: isDeposit ? "" : formData.notes.trim(),
      });

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Transaction error:", error);
      toast.error(error.message || "Failed to add transaction");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Transaction</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Type
            </label>
            <Select
              name="transactionType"
              value={formData.transactionType}
              onValueChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  transactionType: value,
                  ...(value === "deposit"
                    ? { playerName: "", playerNumber: "", notes: "" }
                    : {}),
                }))
              }
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="withdraw">Withdrawal</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Amount *
            </label>
            <Input
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              className="w-full h-10"
              min="0.01"
              step="0.01"
              required
            />
          </div>

          {!isDeposit && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Player Name
                </label>
                <Input
                  name="playerName"
                  value={formData.playerName}
                  onChange={handleInputChange}
                  placeholder="Enter player's name"
                  className="w-full h-10"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Promotion *
                </label>
                <Select value={selectedPromo || undefined} onValueChange={handlePromotionSelect}>
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder="Choose a promotion" />
                  </SelectTrigger>
                  <SelectContent>
                    {promoOptions.length === 0 ? (
                      <SelectItem value="select" disabled>
                        No promotions configured
                      </SelectItem>
                    ) : (
                      promoOptions.map((promotion) => (
                        <SelectItem key={promotion} value={promotion}>
                          {promotion}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  Admin manages this list via the Admin button on Promos.
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Promotion Details
                  <span className="ml-1 font-normal text-gray-400">(optional adjust)</span>
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Selected promotion appears here (you can adjust if needed)"
                  className="w-full min-h-[80px] p-3 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={
                submitting ||
                !formData.amount ||
                (!isDeposit && !formData.notes.trim())
              }
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isDeposit ? (
                "Deposit"
              ) : (
                "Withdrawal"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
